/**
 * DangoteClimateSnapshot — Climate & Circularity view with BU/Asset drilldown.
 * Route: /corporate/climate
 * Reads from DangoteContext consolidated data.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { ChevronRight, TrendingDown, Recycle } from "lucide-react";

export default function DangoteClimateSnapshot() {
  const navigate  = useNavigate();
  const { consolidated, period, byBu, loading } = useDangote();
  const [selectedBU, setSelectedBU] = useState(null);

  const activeBU = selectedBU
    ? byBu?.find(b => b.bu_id === selectedBU)
    : null;

  const total   = consolidated?.totals?.total || 0;
  const totals  = consolidated?.totals || {};

  if (loading && !consolidated) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Climate & Circularity" />
          <p className="text-xs text-slate-400 mt-0.5">
            {period?.label} · All values from approved monthly calculation outputs
          </p>
        </div>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200 flex-shrink-0 ml-4">
          {period?.status?.toUpperCase()}
        </span>
      </div>

      {/* Corporate KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <MetricCard label="Total Scope 1+2+3" value={`${Number(total/1000).toFixed(0)}K`} unit="tCO₂e/month" color="text-slate-900" />
        <MetricCard label="Scope 1" value={`${Number((totals.scope1||0)/1000).toFixed(0)}K`} unit="tCO₂e" color="text-red-700" />
        <MetricCard label="Scope 2" value={`${Number((totals.scope2||0)/1000).toFixed(0)}K`} unit="tCO₂e" color="text-orange-600" />
      </div>

      {/* BU Drilldown */}
      <SectionCard>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Select Business Unit</p>
          {selectedBU && (
            <button onClick={() => setSelectedBU(null)} className="text-xs text-slate-500 hover:text-teal-700">
              ← All BUs
            </button>
          )}
        </div>

        {!selectedBU ? (
          // Corporate view — all BUs
          <div className="space-y-3">
            {(byBu||[]).map(bu => {
              const pct = total > 0 ? Math.min((bu.total/total)*100, 100) : 0;
              const BU_COLORS = {cement:"bg-red-400",refinery:"bg-orange-400",fertilizer:"bg-yellow-400",sugar:"bg-teal-400",logistics:"bg-blue-400"};
              return (
                <button key={bu.bu_id} onClick={() => setSelectedBU(bu.bu_id)}
                  className="w-full text-left group hover:opacity-80">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-semibold text-slate-700 group-hover:text-teal-700 flex items-center gap-1">
                      {bu.bu_name} <ChevronRight size={10} className="opacity-0 group-hover:opacity-100 text-teal-500" />
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400">S1:{Number(bu.scope1||0).toLocaleString()}</span>
                      <span className="font-bold text-slate-900">{Number(bu.total||0).toLocaleString()} tCO₂e</span>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${BU_COLORS[bu.sector_type]||"bg-slate-400"}`} style={{width:`${pct}%`}} />
                  </div>
                  <p className="text-[9px] text-slate-400 mt-0.5 text-right">{pct.toFixed(1)}% of total</p>
                </button>
              );
            })}
          </div>
        ) : activeBU ? (
          // BU detail view with assets
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div>
                <p className="text-sm font-bold text-slate-900">{activeBU.bu_name}</p>
                <p className="text-xs text-slate-400">{Number(activeBU.total||0).toLocaleString()} tCO₂e/month</p>
              </div>
            </div>
            {/* BU Scope bars */}
            {[
              {l:"Scope 1",v:activeBU.scope1||0,c:"bg-red-400"},
              {l:"Scope 2",v:activeBU.scope2||0,c:"bg-orange-400"},
              ...(activeBU.scope3>0?[{l:"Scope 3",v:activeBU.scope3||0,c:"bg-purple-400"}]:[]),
            ].map(({l,v,c}) => {
              const max = activeBU.total || 1;
              return (
                <div key={l} className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 font-semibold">{l}</span>
                    <span className="font-bold text-slate-900">{Number(v).toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${c}`} style={{width:`${Math.min(v/max*100,100)}%`}} />
                  </div>
                </div>
              );
            })}
            {/* Asset list */}
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mt-3">Assets</p>
            {(activeBU.assets||[]).map(a => (
              <button key={a.asset_id} onClick={() => navigate(`/corporate/asset/${a.asset_id}`)}
                className="w-full flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0 hover:opacity-80 group text-left">
                <div>
                  <p className="text-sm font-semibold text-slate-900 group-hover:text-teal-700">{a.asset_id}</p>
                  {a.circularity?.afr_substitution_pct > 0 && (
                    <p className="text-xs text-teal-600">AFR: {a.circularity.afr_substitution_pct.toFixed(1)}% · C/C: {a.circularity.clinker_to_cement_ratio?.toFixed(3)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-bold text-red-700">{Number(a.total||0).toLocaleString()}</p>
                    <p className="text-xs text-slate-400">tCO₂e/month</p>
                  </div>
                  <ChevronRight size={13} className="text-slate-300 group-hover:text-teal-500" />
                </div>
              </button>
            ))}
          </div>
        ) : null}
      </SectionCard>

      {/* Action */}
      {/* CTA: Top reduction levers → Value Bridge */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-emerald-800">Top reduction levers identified</p>
          <p className="text-xs text-emerald-600 mt-0.5">
            {Number((consolidated?.opportunities?.total_reduction_tco2e_yr||0)/1000).toFixed(0)}K tCO₂e/yr reduction potential across {consolidated?.opportunities?.count||0} opportunities
          </p>
        </div>
        <button onClick={() => navigate("/corporate/value-bridge")}
          className="px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 whitespace-nowrap flex-shrink-0 ml-4">
          View in Value Bridge →
        </button>
      </div>

      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Next Actions</p>
        {[
          ["Carbon Value Bridge",  "/corporate/value-bridge"],
          ["SDG Contribution",     "/corporate/sdg"],
          ["Carbon Strategy",      "/corporate/carbon-strategy"],
        ].map(([l,p]) => (
          <button key={p} onClick={() => navigate(p)}
            className="w-full flex items-center justify-between py-2 text-xs text-slate-600 hover:text-teal-700 group">
            {l} <ChevronRight size={11} className="text-slate-300 group-hover:text-teal-500" />
          </button>
        ))}
      </SectionCard>
    </div>
  );
}
